import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./forgotPassword.css";
import { Mail } from "lucide-react";
import register_mark from "../../assets/register_mark.png";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};


export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const navigate = useNavigate();

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
  }, []);

  const normalizeEmail = (email) => {
    if (!email) return "";
    return email.trim().toLowerCase();
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const normalizedEmail = normalizeEmail(email);
      
      if (!normalizedEmail) {
        setError(t("auth.errorEmailRequired"));
        setLoading(false);
        return;
      }

      if (!isValidEmail(normalizedEmail)) {
        setError(t("auth.errorInvalidEmail"));
        setLoading(false);
        return;
      }

      const headers = buildHeaders();
      
      const res = await fetch(`${BASE_URL}/auth/recovery-request/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ 
          email: normalizedEmail.toLowerCase(),
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data?.detail || data?.message || t("auth.errorSomethingWrong"));
        return;
      }

      localStorage.setItem("pendingEmail", normalizedEmail);
      localStorage.setItem("recovery_request_time", new Date().toISOString());
      localStorage.setItem("recovery_user_timezone", userTimeZone);
      
      if (window.gtag) {
        window.gtag("event", "password_recovery_requested", {
          email_domain: normalizedEmail.split('@')[1],
        });
      }

      setTimeout(() => navigate("/verification-password"), 1500);
    } catch (err) {
      console.error("Password recovery error:", err);
      setError(t("auth.errorNetwork"));
      
      if (window.gtag) {
        window.gtag("event", "password_recovery_error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (error) setError("");
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && email.trim() && !loading) {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    
    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [email, loading]);

  return (
    <>
      <Helmet>
        <title>{t("auth.forgotPasswordHelmetTitle")}</title>
        <meta name="description" content={t("auth.forgotPasswordHelmetDescription")} />
      </Helmet>
    <div className="forgot-password">
      <main className="forgot-password-main">
        <div className="forgot-password-card">
          <div className="forgot-password-avatar">
            <div className="forgot-password-avatar-circle">
              <img src={register_mark} alt="reset" />
            </div>
          </div>

          <h2 className="forgot-password-title">{t("auth.forgotPasswordTitle")}</h2>
          <p className="forgot-password-subtitle">
            {t("auth.forgotPasswordSubtitle")}
          </p>

          <form onSubmit={handleSubmit} className="forgot-password-form">
            <label className="forgot-password-label">
              {t("auth.emailAddress")} <span className="required">*</span>
            </label>
            <div className="forgot-password-input-wrapper">
              <Mail className="forgot-password-icon" />
              <input
                type="email"
                placeholder={t("auth.enterEmail")}
                value={email}
                onChange={handleEmailChange}
                required
                disabled={loading}
                autoComplete="email"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email.trim() && !loading) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>

            {error && <p className="error-text">{error}</p>}
            <button 
              type="submit" 
              className="forgot-password-btn" 
              disabled={loading || !email.trim()}
            >
              {loading ? t("auth.sending") : t("auth.sendCode")}
            </button>
          </form>
        </div>
      </main>
    </div>
    </>
  );
}